import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useUpgradeStore } from '../store/upgradeStore';

// Use a function to safely access import.meta.env
const getApiBaseUrl = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL as string;
  }
  return 'http://localhost:3000/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
  retryAfter?: number;
  rawError?: any;
}

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private activeStoreId: string | null = null;
  private tokenRefreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Proactive token refresh
        const token = await this.ensureValidToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add subdomain as tenant header
        const subdomain = this.getSubdomain();
        if (subdomain && !['www', 'admin', 'dashboard'].includes(subdomain)) {
          config.headers['X-Tenant-Subdomain'] = subdomain;
        }

        // Always resolve the latest store context at request time.
        const requestStoreId = localStorage.getItem('activeStoreId') || this.activeStoreId;
        this.activeStoreId = requestStoreId;
        if (requestStoreId) {
          config.headers['X-Store-Id'] = requestStoreId;
          if (import.meta.env.DEV) {
            console.log(`[API CLIENT] Sending X-Store-Id: ${requestStoreId} for ${config.method?.toUpperCase()} ${config.url}`);
          }
        }

        // Add user local timezone
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) {
            config.headers['X-Timezone'] = tz;
          }
        } catch (e) {
          // Ignore
        }

        // Log POST/PUT/PATCH requests for debugging in development only
        if (import.meta.env.DEV && config.method && ['post', 'put', 'patch'].includes(config.method.toLowerCase())) {
          console.log(`=== ${config.method.toUpperCase()} REQUEST ===`);
          console.log('URL:', config.url);
          console.log('Data:', JSON.stringify(config.data, null, 2));
          console.log('Headers:', config.headers);
          console.log('=====================================');
        }

        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle 401 errors (unauthorized)
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't try to refresh if the request was to the refresh endpoint itself
          if (originalRequest.url?.includes('/auth/refresh')) {
            console.error('[AUTH] Refresh token expired or invalid');
            this.clearTokens();
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            // Use the shared refresh promise
            const newAccessToken = await this.refreshAccessToken();
            if (newAccessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.client(originalRequest);
            } else {
              // Refresh failed, clear and redirect
              this.clearTokens();
              if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
              }
              return Promise.reject(error);
            }
          } catch (refreshError) {
            console.error('[AUTH] Token refresh failed:', refreshError);
            this.clearTokens();
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle 403 errors (forbidden - plan limits)
        if (error.response?.status === 403) {
          const data = error.response.data as any;
          const errorCode = data.error?.code || data.code;
          
          if (errorCode === 'STORE_LIMIT_REACHED') {
            useUpgradeStore.getState().open('store_limit');
          }
        }

        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  private getSubdomain(): string | null {
    const hostname = window.location.hostname;

    // Handle localhost subdomains (e.g., naivas.localhost, merchant.localhost)
    if (hostname.includes('.localhost')) {
      const parts = hostname.split('.');
      return parts[0]; // Return the subdomain part (naivas, merchant, etc.)
    }

    // Handle regular domains (e.g., naivas.example.com)
    const parts = hostname.split('.');

    // localhost without subdomain or IP address
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      // Check if VITE_DEV_SUBDOMAIN is set in environment
      if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEV_SUBDOMAIN) {
        return import.meta.env.VITE_DEV_SUBDOMAIN as string;
      }
      return null;
    }

    // Need at least 3 parts for a subdomain (subdomain.domain.com)
    if (parts.length < 3) {
      return null;
    }

    return parts[0];
  }

  private async refreshAccessToken(): Promise<string | null> {
    // If a refresh is already in progress, return the existing promise
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    // Try to get refresh token from various sources
    if (!this.refreshToken) {
      this.refreshToken = localStorage.getItem('refreshToken');
    }
    if (!this.refreshToken) {
      try {
        const stored = localStorage.getItem('auth-storage');
        if (stored) {
          const parsed = JSON.parse(stored);
          this.refreshToken = parsed?.state?.refreshToken || null;
        }
      } catch { /* ignore */ }
    }

    if (!this.refreshToken) {
      console.error('[AUTH] No refresh token available — cannot refresh');
      return null;
    }

    // Create a new refresh promise
    this.tokenRefreshPromise = (async () => {
      try {
        console.log('[AUTH] refreshing access token...');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: this.refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;

        // Update tokens
        this.setAccessToken(accessToken);
        if (newRefreshToken) {
          this.setRefreshToken(newRefreshToken);
          // Sync with Zustand storage
          try {
            const stored = localStorage.getItem('auth-storage');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed?.state) {
                parsed.state.token = accessToken;
                parsed.state.refreshToken = newRefreshToken;
                localStorage.setItem('auth-storage', JSON.stringify(parsed));
              }
            }
          } catch { /* ignore */ }
        }

        console.log('[AUTH] Token refreshed successfully');
        return accessToken;
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401) {
          console.error('[AUTH] Refresh token rejected by server');
          this.clearTokens();
        } else {
          console.error('[AUTH] Token refresh error:', error);
        }
        return null;
      } finally {
        // Clear the promise when done
        this.tokenRefreshPromise = null;
      }
    })();

    return this.tokenRefreshPromise;
  }

  private normalizeError(error: AxiosError): ApiError {
    if (error.response?.data) {
      const data = error.response.data as any;

      // Log the raw error for debugging
      console.log('Raw API Error Response:', data);

      return {
        message: data.message || data.error?.message || data.error || 'An error occurred',
        errors: data.errors || data.error?.errors,
        statusCode: error.response.status,
        retryAfter: (() => {
          const value = error.response?.headers?.['retry-after'];
          const seconds = Number(value);
          return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
        })(),
        rawError: data, // Include raw error for debugging
      };
    }

    if (error.request) {
      // Log full error details for network errors
      console.error('Network Error Details:', {
        message: error.message,
        code: error.code,
        request: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
        },
        error: error,
      });

      return {
        message: `Network error: ${error.message || 'Please check your connection.'}`,
        statusCode: 0,
        rawError: error,
      };
    }

    return {
      message: error.message || 'An unexpected error occurred',
    };
  }

  // Token management
  public setAccessToken(token: string): void {
    this.accessToken = token;
    localStorage.setItem('accessToken', token);
  }

  public setRefreshToken(token: string): void {
    this.refreshToken = token;
    localStorage.setItem('refreshToken', token);
  }

  public getAccessToken(): string | null {
    return this.accessToken || localStorage.getItem('accessToken');
  }

  public getRefreshToken(): string | null {
    return this.refreshToken || localStorage.getItem('refreshToken');
  }

  public clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.activeStoreId = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('activeStoreId');
  }

  public setActiveStoreId(storeId: string | null): void {
    this.activeStoreId = storeId;
    if (storeId) {
      localStorage.setItem('activeStoreId', storeId);
    } else {
      localStorage.removeItem('activeStoreId');
    }
  }

  /**
   * Decodes JWT payload without a library
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  /**
   * Checks if token is expired or close to expiring (within 30s buffer)
   */
  public isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    // Buffer of 30 seconds to prevent race conditions at expiry edge
    const buffer = 30; 
    return (decoded.exp - buffer) < (Date.now() / 1000);
  }

  /**
   * Ensures we have a valid access token, refreshing if necessary.
   * Useful for SSE or other non-Axios requests.
   */
  public async ensureValidToken(): Promise<string | null> {
    const token = this.getAccessToken();
    if (!token || this.isTokenExpired(token)) {
      console.log('[AUTH] Proactively refreshing expired/missing token');
      return await this.refreshAccessToken();
    }
    return token;
  }

  public initializeTokens(): void {
    // Step 1: Try raw localStorage keys first (fastest path)
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedActiveStoreId = localStorage.getItem('activeStoreId');

    if (storedAccessToken) this.accessToken = storedAccessToken;
    if (storedRefreshToken) this.refreshToken = storedRefreshToken;
    if (storedActiveStoreId) this.activeStoreId = storedActiveStoreId;

    // Step 2: Fall back to Zustand's persisted JSON blob to cover the case
    // where raw keys are missing but the Zustand state is still persisted.
    // This closes the race window between module init and Zustand rehydration.
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        const state = parsed?.state;
        if (state?.token && !this.accessToken) {
          this.accessToken = state.token;
          localStorage.setItem('accessToken', state.token);
        }
        if (state?.refreshToken && !this.refreshToken) {
          this.refreshToken = state.refreshToken;
          localStorage.setItem('refreshToken', state.refreshToken);
        }
      }
    } catch (e) {
      // Ignore parse errors
    }

    if (import.meta.env?.DEV && this.accessToken) {
      console.log('[AUTH CLIENT] Tokens loaded from storage on init');
    } else if (import.meta.env?.DEV) {
      console.log('[AUTH CLIENT] No tokens found in storage — user is logged out');
    }
  }

  // HTTP methods
  public async get<T>(url: string, config = {}): Promise<T> {
    const response = await this.client.get<any>(url, config);
    // Handle backend's {success, data, message} format
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data as T;
    }
    return response.data;
  }

  public async post<T>(url: string, data?: any, config = {}): Promise<T> {
    const response = await this.client.post<any>(url, data, config);
    // Handle backend's {success, data, message} format
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data as T;
    }
    return response.data;
  }

  public async put<T>(url: string, data?: any, config = {}): Promise<T> {
    const response = await this.client.put<any>(url, data, config);
    // Handle backend's {success, data, message} format
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data as T;
    }
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config = {}): Promise<T> {
    const response = await this.client.patch<any>(url, data, config);
    // Handle backend's {success, data, message} format
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data as T;
    }
    return response.data;
  }

  public async delete<T>(url: string, config = {}): Promise<T> {
    const response = await this.client.delete<any>(url, config);
    // Handle backend's {success, data, message} format
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data as T;
    }
    return response.data;
  }

  public getClient(): AxiosInstance {
    return this.client;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Initialize tokens on module load
apiClient.initializeTokens();
